import React from "react";
import { getPaginationMeta, PAGE_SIZE_OPTIONS } from "./paginationUtils";
import "./ListPagination.css";

type ListPaginationProps = {
  page: number;
  totalCount: number;
  pageSize: number;
  currentCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  visible?: boolean;
  emptyMessage?: string;
  showingRecords?: string;
  pageInfo?: string;
  nextPage?: string | null;
  prevPage?: string | null;
  className?: string;
};

const ListPagination: React.FC<ListPaginationProps> = ({
  page,
  totalCount,
  pageSize,
  currentCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  visible = true,
  emptyMessage = "No records found",
  showingRecords,
  pageInfo,
  nextPage,
  prevPage,
  className = "",
}) => {
  if (!visible) return null;

  const { totalPages, rangeStart, rangeEnd, page: safePage } = getPaginationMeta(
    page,
    totalCount,
    pageSize,
    currentCount
  );
  const resolvedTotalPages = totalPages > 0 ? totalPages : Math.ceil(totalCount / pageSize) || 0;
  const canGoPrev = prevPage !== undefined ? !!prevPage : safePage > 1;
  const canGoNext = nextPage !== undefined ? !!nextPage : safePage < resolvedTotalPages;
  const recordsLabel =

    (totalCount > 0
      ? `Showing ${rangeStart}-${rangeEnd} of ${totalCount} records`
      : emptyMessage);
  const pageLabel =
    pageInfo ?? (resolvedTotalPages > 0 ? `Page ${safePage} of ${resolvedTotalPages}` : "");

  const pageSizeSelector = onPageSizeChange ? (
    <div className="list-pagination-page-size list-pagination-page-size-top">
      <label htmlFor={`page-size-${safePage}`}>Rows per page</label>
      <select
        id={`page-size-${safePage}`}
        className="form-select form-select-sm"
        value={pageSize}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
      >
        {pageSizeOptions.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </div>
  ) : null;

  return (
    <>
      {pageSizeSelector}

      <div className={`list-pagination-bar ${className}`.trim()}>
        <div className="list-pagination-info">
          {totalCount > 0 ? (
            <>
              <span>{recordsLabel}</span>
              {pageLabel && (
                <>
                  <span className="separator">|</span>
                  <span>{pageLabel}</span>
                </>
              )}
            </>
          ) : (
            <span>{showingRecords || emptyMessage}</span>
          )}
        </div>

        <div className="list-pagination-actions">
          {resolvedTotalPages > 1 && (
            <div className="list-pagination-controls">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={!canGoPrev}
                onClick={() => onPageChange(safePage - 1)}
              >
                Prev
              </button>

              {Array.from({ length: resolvedTotalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`btn btn-sm ${p === safePage ? "btn-primary" : "btn-outline-secondary"
                    }`}
                  onClick={() => onPageChange(p)}
                >
                  {p}
                </button>
              ))}

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={!canGoNext}
                onClick={() => onPageChange(safePage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ListPagination;
